import type { ComponentProps } from "react";
import type { Exercise, Plan, Program, UnitsPreference } from "@jackedlogs/shared";

import type { UiTheme } from "../theme";
import type { LibraryItemCard } from "./LibraryItemCard";
import {
  libraryFavoriteKey,
  type LibraryItem,
  type LibraryState,
} from "./useLibrary";

// The <LibraryItemCard> prop bag, derived per row. Split out of
// LibraryBrowser's renderItem to keep that file under the 500-line cap.
// Pure derivation: same props, same guards, same order — the grid-cell
// wrapper and the `key` stay in LibraryBrowser, which owns layout.
//
// ⚠️ Almost every callback here is OPTIONAL on LibraryItemCard and is
// computed by a ternary that can yield undefined. Dropping or loosening one
// of those guards still compiles and silently stops rendering that action,
// so the guards below are copied verbatim rather than rewritten.
export function buildLibraryItemCardProps(ctx: {
  item: LibraryItem;
  theme: UiTheme;
  dark: boolean;
  catalog: Exercise[] | null;
  resolveName: (slug: string, snapshotFallback?: string) => string;
  unit: UnitsPreference;
  // Narrowed to the five members actually read, mirroring the sibling
  // useLibraryActions (which takes Pick<LibraryState, ...>) rather than the
  // whole hook surface.
  lib: Pick<
    LibraryState,
    | "cloningKey"
    | "alreadyCloned"
    | "favoriteKeys"
    | "favoritePendingKey"
    | "cloneItem"
  >;
  followingKey: string | null;
  mutatingKey: string | null;
  startingPlanId?: string | null;
  onFollow: (item: LibraryItem) => void | Promise<void>;
  onStar: (item: LibraryItem) => void | Promise<void>;
  onSetStatus: (
    item: LibraryItem,
    status: Program["status"],
  ) => void | Promise<void>;
  onStartPlan?: (plan: Plan) => void;
  onEditProgram?: (program: Program) => void;
  onEditPlan?: (plan: Plan) => void;
  requestDelete: (item: LibraryItem) => void;
  confirmDelete?: (item: LibraryItem, run: () => void) => void;
}): ComponentProps<typeof LibraryItemCard> {
  const {
    item,
    theme,
    dark,
    catalog,
    resolveName,
    unit,
    lib,
    followingKey,
    mutatingKey,
    startingPlanId,
    onFollow,
    onStar,
    onSetStatus,
    onStartPlan,
    onEditProgram,
    onEditPlan,
    requestDelete,
    confirmDelete,
  } = ctx;
  const favKey = libraryFavoriteKey(item);
  const personal = item.source === "personal";
  const personalProgram = personal && item.kind === "program";
  const personalPlan = personal && item.kind === "plan";
  return {
    theme,
    dark,
    item,
    catalog,
    resolveName,
    unit,
    cloning: lib.cloningKey === item.key,
    cloneBusy: lib.cloningKey !== null && lib.cloningKey !== item.key,
    alreadyCloned: lib.alreadyCloned(item),
    following: followingKey === item.key,
    favorited: favKey !== null && lib.favoriteKeys.has(favKey),
    favoriteBusy: favKey !== null && lib.favoritePendingKey === favKey,
    onClone: (i) => void lib.cloneItem(i),
    onFollow:
      personalProgram && item.program.status === "archived"
        ? () => void onFollow(item)
        : undefined,
    onToggleFavorite: favKey !== null ? () => void onStar(item) : undefined,
    onStart:
      personalPlan && onStartPlan && item.plan.status !== "completed"
        ? () => onStartPlan(item.plan)
        : undefined,
    starting: personalPlan && startingPlanId === item.plan.id,
    onPublish:
      personalProgram && item.program.status === "draft"
        ? () => void onSetStatus(item, "archived")
        : undefined,
    onUnfollow:
      personalProgram && item.program.status === "active"
        ? () => void onSetStatus(item, "archived")
        : undefined,
    statusBusy: mutatingKey === item.key,
    onEdit:
      personalProgram && onEditProgram
        ? () => onEditProgram(item.program)
        : personalPlan && onEditPlan
          ? () => onEditPlan(item.plan)
          : undefined,
    // Followed programs are unfollow-only (the ProgramsPlanView
    // contract) — delete only from draft/published, and plans.
    onDelete:
      personalPlan || (personalProgram && item.program.status !== "active")
        ? () => requestDelete(item)
        : undefined,
    confirmDelete: confirmDelete ? "host" : "inline",
  };
}
